import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AdminShell } from "@/components/layout/AdminShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/admin/Dashboard";
import UsersList from "@/pages/admin/UsersList";
import UserEditor from "@/pages/admin/UserEditor";
import ClientsList from "@/pages/admin/ClientsList";
import ClientEditor from "@/pages/admin/ClientEditor";
import JobsList from "@/pages/admin/JobsList";
import JobDetail from "@/pages/admin/JobDetail";
import JobAudit from "@/pages/admin/JobAudit";
import Schedule from "@/pages/admin/Schedule";
import Approvals from "@/pages/admin/Approvals";
import Reports from "@/pages/admin/Reports";
import Settings from "@/pages/admin/Settings";
import PhasePlaceholder from "@/pages/stubs/PhasePlaceholder";

// Timesheet
import WeeklyGrid from "@/pages/timesheet/WeeklyGrid";
import History from "@/pages/timesheet/History";

// Mobile
import { MobileShell } from "@/pages/mobile/MobileShell";
import Today from "@/pages/mobile/Today";
import MobileJobDetail from "@/pages/mobile/JobDetail";
import Stopped from "@/pages/mobile/Stopped";
import AddJob from "@/pages/mobile/AddJob";
import Week from "@/pages/mobile/Week";
import Profile from "@/pages/mobile/Profile";
import MobileJobs from "@/pages/mobile/MobileJobs";
import { isMobileDevice } from "@/lib/mobileDetect";

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    // Staff on mobile → /m/today. Everyone else → /admin.
    if (user.role === "staff" || isMobileDevice()) return <Navigate to="/m/today" replace />;
    return <Navigate to="/admin" replace />;
}

function App() {
    // Register service worker + set manifest
    useEffect(() => {
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.register("/sw.js").catch(() => {});
        }
    }, []);
    return (
        <AuthProvider>
            <Toaster richColors position="top-right" />
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/login" element={<Login />} />

                    <Route path="/admin" element={<AdminShell />}>
                        <Route index element={<Dashboard />} />
                        <Route path="users" element={<UsersList />} />
                        <Route path="users/:id" element={<UserEditor />} />
                        <Route path="clients" element={<ClientsList />} />
                        <Route path="clients/:id" element={<ClientEditor />} />
                        <Route path="jobs" element={<JobsList />} />
                        <Route path="jobs/:id" element={<JobDetail />} />
                        <Route path="jobs/:id/audit" element={<JobAudit />} />
                        <Route path="schedule" element={<Schedule />} />
                        <Route path="approvals" element={<Approvals />} />
                        <Route path="reports" element={<Reports />} />
                        <Route path="settings" element={<Settings />} />
                    </Route>

                    <Route path="/m" element={<MobileShell />}>
                        <Route index element={<Navigate to="/m/today" replace />} />
                        <Route path="today" element={<Today />} />
                        <Route path="jobs" element={<MobileJobs />} />
                        <Route path="job/:jobId" element={<MobileJobDetail />} />
                        <Route path="stopped/:entryId" element={<Stopped />} />
                        <Route path="add" element={<AddJob />} />
                        <Route path="week" element={<Week />} />
                        <Route path="profile" element={<Profile />} />
                    </Route>

                    <Route path="/timesheet" element={<AdminShell />}>
                        <Route index element={<WeeklyGrid />} />
                        <Route path="history" element={<History />} />
                    </Route>

                    <Route
                        path="/schedule/*"
                        element={<PhasePlaceholder title="Legacy schedule redirect" phase="Phase 4" />}
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
